import { Component, OnInit, ViewChild } from '@angular/core';
import { Integration } from '../interfaces/integration';
import { PuzzleService } from '../services/puzzle.service';
import { ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UserLimits } from '../interfaces/UserLimits';
import { Country } from '../interfaces/country';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DemoRequest } from '../interfaces/DemoRequest';
import { ResponseSmallDialogComponent } from '../dialogs/response-small-dialog/response-small-dialog.component';
import { RecaptchaComponent } from 'ng-recaptcha';
import { RecaptchaLoaderService } from '../services/recaptcha-loader.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LocalService } from '../services/local.service';

@Component({
  selector: 'app-integrations-id',
  templateUrl: './integrations-id.component.html',
  styleUrls: ['./integrations-id.component.css'],
})
export class IntegrationsIdComponent implements OnInit {
  imgPath: string = 'https://webapi.haibuhr.com';
  integration: Integration;

  demoForm: FormGroup;
  userLimits: UserLimits[];
  countries: Country[];
  recapcha: string;
  sending: boolean = false;
  sendText: string = 'Siguiente';
  overview: SafeResourceUrl;
  demoRequest: DemoRequest = {} as DemoRequest;
  recaptchaReady = false;
  activeLang: string = 'Es';
  @ViewChild('captchaComponent') captchaComponent: RecaptchaComponent;
  @ViewChild('nameInput') nameInput: any;

  constructor(
    private puzzleService: PuzzleService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private translate: TranslateService,
    private recaptchaLoader: RecaptchaLoaderService,
    private localService: LocalService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initializeLanguage();
    this.getIntegration();
    this.createForm();
    this.getUserLimits();
    this.getCountries();

    this.recaptchaLoader.isRecaptchaReady.subscribe((ready) => {
      this.recaptchaReady = ready;
    });

    this.translate.onLangChange.subscribe((event) => {
      this.activeLang = event.lang == 'esp' ? 'Es' : 'En';
      this.getIntegration();
    });
  }

  initializeLanguage() {
    if (this.localService.getJsonValue('lang')) {
      this.translate.use(this.localService.getJsonValue('lang'));
      this.activeLang = this.localService.getJsonValue('lang') == 'eng' ? 'En' : 'Es';
    } else {
      const browserLang = this.translate.getBrowserLang();
      let appLang = this.mapBrowserLangToAppLang(browserLang);
      this.translate.use(appLang);
      this.activeLang = appLang == 'eng' ? 'En' : 'Es';
      this.localService.setJsonValue('lang', appLang);
    }
  }

  mapBrowserLangToAppLang(browserLang: string): string {
    const langMap = {
      en: 'eng',
      es: 'esp',
    };
    return langMap[browserLang] || 'eng';
  }

  public noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length ? null : { whitespace: true };
  }

  public noSpecialCharactersValidator(control: FormControl) {
    const value = control.value || '';
    // Expresión regular que permite letras (de cualquier alfabeto), números y espacios en blanco
    const regex = /^[\p{L}\p{N}\s.]*$/u;

    if (!regex.test(value)) {
      return { noSpecialCharacters: true };
    }

    return null;
  }

  public isEmailValid(control: FormControl) {
    const value = control.value || '';
    // Expresión regular que permite letras (de cualquier alfabeto), números y espacios en blanco
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!regex.test(value)) {
      return { isEmailValid: true };
    }

    return null;
  }

  public isPhoneValid(control: FormControl) {
    const value = control.value || '';
    const regex = /^[0-9]*$/;

    if (!regex.test(value)) {
      return { isPhoneValid: true };
    }

    if (value.length === 10 && value.substring(0, 3) === '000') {
      return { isPhoneValid: true };
    }

    return null;
  }

  getIntegration() {
    const params = {
      V_NAME: this.activatedRoute.snapshot.params['id'],
      V_LANGUAGE: this.activeLang,
    };
    this.puzzleService.getIntegrationByName(params).subscribe((res) => {
      this.integration = res.data;
      this.overview = this.sanitizer.bypassSecurityTrustHtml(this.integration.hI_OVERVIEW);
    });
  }

  createForm() {
    this.demoForm = this.fb.group({
      names: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator,
          this.noSpecialCharactersValidator,
        ],
      ],
      lastnames: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator,
          this.noSpecialCharactersValidator,
        ],
      ],
      email: ['', [Validators.required, Validators.email, this.isEmailValid]],
      phone: ['', [Validators.required, this.isPhoneValid]],
      company: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator,
          this.noSpecialCharactersValidator,
        ],
      ],
      userLimit: ['', Validators.required],
      country: ['', Validators.required],
      terms: [false, Validators.requiredTrue],
    });
  }

  getUserLimits() {
    this.puzzleService.getUserLimits().subscribe((response) => {
      this.userLimits = response;
      // this.checkScreenSize();
    });
  }

  resolved(captchaResponse: string) {
    this.recapcha = captchaResponse;
  }

  getCountries() {
    this.puzzleService.getDemoCountries().subscribe((response) => {
      this.countries = response;
    });
  }

  sendMail() {
    if (this.sending) {
      return;
    }
    this.sending = true;
    this.sendText = 'Enviando';

    if (!this.demoForm.valid) {
      this.openSmallResponseDialog(
        'Error',
        this.translate.instant('loginAlertForm'),
      );
      Object.values(this.demoForm.controls).forEach((control) => {
        control.markAsTouched();
      });
      this.sending = false;
      this.sendText = 'Siguiente';
    } else {
      if (this.recapcha == null || this.recapcha == '') {
        this.openSmallResponseDialog(
          'Error',
          this.translate.instant('pleaseAcceptRecaptcha'),
        );
        this.sending = false;
        this.sendText = 'Siguiente';
      } else {
        if (!this.demoForm.get('terms').value) {
          this.openSmallResponseDialog(
            'Error',
            this.translate.instant('pleaseAcceptTerms'),
          );
          this.sending = false;
          this.sendText = 'Siguiente';
        } else {
          this.demoRequest = {
            Names: this.demoForm.get('names').value,
            LastNames: this.demoForm.get('lastnames').value,
            Email: this.demoForm.get('email').value,
            CellPhone: this.demoForm.get('phone').value,
            CompanyName: this.demoForm.get('company').value,
            UserLimit: this.demoForm.get('userLimit').value,
            CountryName: this.demoForm.get('country').value,
            TargetCompany: this.integration.hI_NAME,
            TermsAndConditionsAccepted: true,
            Recaptcha: this.recapcha,
          };
          this.puzzleService.integrationRequest(this.demoRequest).subscribe(
            (response) => {
              if (response.isSuccess) {
                if (
                  response.message == 'Questionnaire sent successfully'
                ) {
                  response.message = this.translate.instant(
                    'loginDemoRequestSuccess',
                  );
                }
                this.openSmallResponseDialog('Exito', response.message);
                this.demoRequest = {} as DemoRequest;
                this.demoForm.reset();
              } else {
                this.openSmallResponseDialog('Error', this.translate.instant('loginDemoRequestError'));
              }
              this.sending = false;
              this.sendText = 'Siguiente';
              this.captchaComponent.reset();
              this.demoForm.reset();
            },
            (error) => {
              this.openSmallResponseDialog('Error', error);
              this.sending = false;
              this.sendText = 'Siguiente';
              this.captchaComponent.reset();
            },
          );
        }
      }
    }
  }

  openSmallResponseDialog(response: string, status?: string) {
    let data: string[] = [];
    if (status) {
      data.push(response);
      data.push(status);
    } else {
      if (response == 'Error') {
        data.push('Error');
        data.push('Ocurrió un error, favor de intentarlo nuevamente');
      } else if (response == 'Exito') {
        data.push('Exito');
        data.push('La empresa ha sido creada correctamente');
      }
    }

    const dialogRef = this.dialog.open(ResponseSmallDialogComponent, {
      width: '500px',
      /* height: '400px', */
      data: data,
    });
  }

  scrollToElement(element: string) {
    this.smoothScrollToElement(element);
  }

  smoothScrollToElement(element: string): Promise<void> {
    return new Promise((resolve) => {
      const targetElement = document.getElementById(element);
      const navbarHeight = 60;

      if (targetElement) {
        const offsetTop = targetElement.offsetTop - navbarHeight;

        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });

        // A small delay to ensure the scroll has finished
        setTimeout(() => {
          resolve();
          if (element === 'demo' && this.nameInput?.nativeElement) {
            this.nameInput.nativeElement.focus();
          }
        }, 1000); // Adjust the delay as needed
      } else {
        resolve();
      }
    });
  }
}
